import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import GiftIcon from "@redotech/redo-icons/gift-01.svg";
import { LoggedInLoyaltyInfo } from "@redotech/redo-loyalty-common/storefront-popup";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as css from "./logged-in-popup.module.css";
import { PopupHeader } from "./popup-header";
import { useAppliedDiscountState } from "./use-applied-discount-state";

export enum LoggedInPopupState {
  PRE_REDEMPTION = "PRE_REDEMPTION",
  REDEEMED_APPLICATION_SUCCESSFUL = "REDEEMED_APPLICATION_SUCCESSFUL",
  REDEEMED_APPLICATION_FAILED = "REDEEMED_APPLICATION_FAILED",
}

type LoggedInLoyaltyProps = { loyaltyInfo: LoggedInLoyaltyInfo } & (
  | { readonly: true }
  | {
      readonly: false;
      redeemPoints: (
        pointsToUse: number,
      ) => Promise<{ discountCode: string; autoApplySuccessful: boolean }>;
      undoRedeem: () => Promise<void>;
    }
);

export const LoggedInLoyaltyPopup = memo(function LoggedInLoyaltyPopup({
  loyaltyInfo,
  ...props
}: LoggedInLoyaltyProps) {
  const {
    discountCode,
    pointsUsed,
    onDiscountCodeApplied,
    onUndoDiscountCode,
    loggedInPopupState,
    setLoggedInPopupState,
  } = useAppliedDiscountState();

  const [actionPending, setActionPending] = useState(false);

  const onRedeem = useHandler(async (pointsToUse: number) => {
    if (props.readonly) {
      return;
    }

    setActionPending(true);
    try {
      const { discountCode, autoApplySuccessful } =
        await props.redeemPoints(pointsToUse);
      await onDiscountCodeApplied({
        code: discountCode,
        pointsUsed: pointsToUse,
      });

      if (autoApplySuccessful) {
        setLoggedInPopupState(
          LoggedInPopupState.REDEEMED_APPLICATION_SUCCESSFUL,
        );
      } else {
        setLoggedInPopupState(LoggedInPopupState.REDEEMED_APPLICATION_FAILED);
      }
    } catch (error) {
      setLoggedInPopupState(LoggedInPopupState.PRE_REDEMPTION);
      throw error;
    } finally {
      setActionPending(false);
    }
  });

  const onUndoRedeem = useHandler(async () => {
    if (props.readonly) {
      return;
    }

    setActionPending(true);
    try {
      await props.undoRedeem();
      await onUndoDiscountCode();
      setLoggedInPopupState(LoggedInPopupState.PRE_REDEMPTION);
    } finally {
      setActionPending(false);
    }
  });

  return (
    <Flex dir="column" gap="xl" w="full">
      {loggedInPopupState === LoggedInPopupState.PRE_REDEMPTION ? (
        <PreRedemptionPopupContent
          loyaltyInfo={loyaltyInfo}
          onRedeem={onRedeem}
          pointsUsed={pointsUsed}
          redeemPending={actionPending}
        />
      ) : loggedInPopupState ===
          LoggedInPopupState.REDEEMED_APPLICATION_SUCCESSFUL && !!pointsUsed ? (
        <RedeemedApplicationSuccessfulPopupContent
          loyaltyInfo={loyaltyInfo}
          onUndoRedeem={onUndoRedeem}
          pointsUsed={pointsUsed}
          undoPending={actionPending}
        />
      ) : (
        !!discountCode &&
        !!pointsUsed && (
          <RedeemedApplicationFailedPopupContent
            code={discountCode}
            loyaltyInfo={loyaltyInfo}
            onUndoRedeem={onUndoRedeem}
            pointsUsed={pointsUsed}
            undoPending={actionPending}
          />
        )
      )}
      {loyaltyInfo.pointsForPurchase > 0 && (
        <Text fontSize="xs" textColor="secondary">
          You'll earn {loyaltyInfo.pointsForPurchase} points on this purchase.
        </Text>
      )}
      {loyaltyInfo.loyaltyDashboardUrl && (
        <Flex alignSelf="center">
          <Text fontSize="xs" textColor="secondary">
            <a
              className={css.dashboardLink}
              href={loyaltyInfo.loyaltyDashboardUrl}
            >
              View loyalty dashboard →
            </a>
          </Text>
        </Flex>
      )}
    </Flex>
  );
});

const PreRedemptionPopupContent = memo(function PreRedemptionPopupContent({
  loyaltyInfo,
  onRedeem,
  redeemPending,
  pointsUsed,
}: {
  loyaltyInfo: LoggedInLoyaltyInfo;
  onRedeem: (pointsToUse: number) => Promise<void>;
  redeemPending: boolean;
  pointsUsed?: number | undefined;
}) {
  const pointsRemaining = useMemo(() => {
    return loyaltyInfo.pointsBalance - (pointsUsed ?? 0);
  }, [loyaltyInfo.pointsBalance, pointsUsed]);

  const [pointsToUse, _setPointsToUse] = useState<string>(
    pointsRemaining.toString(),
  );
  const setPointsToUse = useHandler((value: string) => {
    if (value === "") {
      _setPointsToUse(value);
      return;
    }

    const pointsToUse = Math.min(parseInt(value), pointsRemaining);
    _setPointsToUse(pointsToUse.toString());
  });

  const handleApply = useHandler(async () => {
    if (pointsRemaining <= 0 || parseInt(pointsToUse) <= 0) {
      return;
    }

    await onRedeem(parseInt(pointsToUse));
  });

  return (
    <Flex dir="column" gap="xl" w="full">
      <PopupHeader
        description={`${pointsRemaining} points • Worth ${moneyFormat({
          amount: loyaltyInfo.singlePointValue.amount * pointsRemaining,
          currency: loyaltyInfo.singlePointValue.currency,
        })}`}
        title={`Hi ${loyaltyInfo.customerName ?? "there"}`}
      />
      {pointsRemaining > 0 && (
        <>
          <Flex align="center" dir="row" gap="md" w="full">
            <Flex grow={1}>
              <RedoTextInput
                description={`= ${moneyFormat({
                  amount:
                    loyaltyInfo.singlePointValue.amount *
                    parseInt(pointsToUse || "0"),
                  currency: loyaltyInfo.singlePointValue.currency,
                })} discount`}
                fullWidth
                inputTypeHint="number"
                label="Points to use"
                max={pointsRemaining}
                min={0}
                setValue={setPointsToUse}
                state={redeemPending ? "disabled" : undefined}
                value={pointsToUse}
              />
            </Flex>
            <RedoButton
              disabled={redeemPending}
              hierarchy="secondary"
              onClick={() => {
                setPointsToUse(pointsRemaining.toString());
              }}
              text="Max"
            />
          </Flex>
          <RedoButton
            flexGrow={1}
            hierarchy="primary"
            IconLeading={GiftIcon}
            onClick={handleApply}
            pending={redeemPending}
            text={`Apply ${pointsToUse} points`}
          />
        </>
      )}
    </Flex>
  );
});

const RedeemedApplicationSuccessfulPopupContent = memo(
  function RedeemedApplicationSuccessfulPopupContent({
    pointsUsed,
    loyaltyInfo,
    onUndoRedeem,
    undoPending,
  }: {
    pointsUsed: number;
    loyaltyInfo: LoggedInLoyaltyInfo;
    onUndoRedeem: () => Promise<void>;
    undoPending: boolean;
  }) {
    return (
      <Flex dir="column" gap="xl" w="full">
        <PopupHeader
          description={`${moneyFormat({
            amount: loyaltyInfo.singlePointValue.amount * pointsUsed,
            currency: loyaltyInfo.singlePointValue.currency,
          })} automatically applied`}
          Icon={CheckCircleIcon}
          iconColor="success"
          title="Discount applied"
        />
        <Flex
          align="center"
          borderRadius="xl"
          className={css.successBadge}
          dir="row"
          justify="space-between"
          p="xl"
          w="full"
        >
          <Flex dir="column" gap="none" py="sm" w="full">
            <Text fontSize="sm" fontWeight="medium" textColor="success">
              {pointsUsed} points applied
            </Text>
            <Text fontSize="xxs" textColor="tertiary">
              {moneyFormat({
                amount: loyaltyInfo.singlePointValue.amount * pointsUsed,
                currency: loyaltyInfo.singlePointValue.currency,
              })}{" "}
              discount
            </Text>
          </Flex>
          <RedoButton
            hierarchy="secondary"
            onClick={onUndoRedeem}
            pending={undoPending}
            size="sm"
            text="Undo"
          />
        </Flex>
      </Flex>
    );
  },
);

const RedeemedApplicationFailedPopupContent = memo(
  function RedeemedApplicationFailedPopupContent({
    pointsUsed,
    loyaltyInfo,
    code,
    undoPending,
    onUndoRedeem,
  }: {
    pointsUsed: number;
    loyaltyInfo: LoggedInLoyaltyInfo;
    code: string;
    undoPending: boolean;
    onUndoRedeem: () => Promise<void>;
  }) {
    const copyButton = useMemo(() => {
      return (
        <CopyIcon
          className={css.copyIcon}
          onClick={async () => {
            await navigator.clipboard.writeText(code);
          }}
        />
      );
    }, [code]);

    return (
      <Flex dir="column" gap="xl" w="full">
        <PopupHeader
          Icon={AlertCircleIcon}
          iconColor="warning"
          title="Use this discount code"
        />
        <Flex
          borderRadius="xl"
          className={css.failureBadge}
          dir="column"
          gap="md"
          p="xl"
          w="full"
        >
          <Flex dir="row" gap="md" w="full">
            <RedoTextInput
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                cursor: "default",
              }}
              fullWidth
              setValue={() => {}}
              state="disabled"
              trailingActions={copyButton}
              value={code}
            />
            <RedoButton
              hierarchy="secondary"
              onClick={onUndoRedeem}
              pending={undoPending}
              size="sm"
              text="Adjust"
            />
          </Flex>
          <Text fontSize="xxs" textColor="tertiary">
            Use discount code at checkout to redeem {pointsUsed} points for a{" "}
            {moneyFormat({
              amount: loyaltyInfo.singlePointValue.amount * pointsUsed,
              currency: loyaltyInfo.singlePointValue.currency,
            })}{" "}
            discount
          </Text>
        </Flex>
      </Flex>
    );
  },
);
